import { createContext, createElement, useContext } from 'react'
import type { ReactNode } from 'react'
import { useMatches, useParams } from 'react-router-dom'
import { z } from 'zod'

import { useActiveAccount } from './activeAccountContext'

const ApiOrgSlugOverrideContext = createContext<string | null>(null)

export interface ApiOrgSlugOverrideProviderProps {
  children: ReactNode
  orgSlug?: string
}

export function ApiOrgSlugOverrideProvider({
  children,
  orgSlug,
}: ApiOrgSlugOverrideProviderProps) {
  return createElement(
    ApiOrgSlugOverrideContext.Provider,
    { value: orgSlug ?? null },
    children,
  )
}

const OrgSlugSchema = z.string()

/**
 * The `org_slug` route param from the current URL, or `null` when the active
 * route is not org-scoped. Use this for resolving the active account; for
 * backend calls prefer the resolved {@link useApiOrgSlug}.
 *
 * Only sees params from this route and its ancestors — not child routes. For
 * providers mounted above `:org_slug`, use {@link useMatchedRouteOrgSlug}.
 */
export function useApiOrgSlugFromParams() {
  const params = useParams()
  return OrgSlugSchema.safeParse(params.org_slug).data ?? null
}

/**
 * `org_slug` from any matched route in the current hierarchy. Works in parent
 * providers (e.g. root-level PhoneProvider) that sit above the `:org_slug` route.
 */
export function useMatchedRouteOrgSlug() {
  const matches = useMatches()
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches.at(index)
    const orgSlug = OrgSlugSchema.safeParse(match?.params.org_slug).data
    if (orgSlug !== undefined) {
      return orgSlug
    }
  }

  return null
}

/** Active account slug for API path params / query keys, or `null`. */
export function useSafeApiOrgSlug() {
  const overrideOrgSlug = useContext(ApiOrgSlugOverrideContext)
  const activeAccount = useActiveAccount()
  if (overrideOrgSlug !== null) {
    return overrideOrgSlug
  }

  return activeAccount?.slug ?? null
}

/**
 * Active account slug for API path params, query keys, and backend-bound work.
 * Throws when there is no active account (i.e. the route is not org-scoped).
 */
export function useApiOrgSlug() {
  const apiOrgSlug = useSafeApiOrgSlug()
  if (apiOrgSlug === null) {
    throw new Error('activeAccount is required but no active account was found')
  }

  return apiOrgSlug
}

export const useSafeRouterOrgSlug = useSafeApiOrgSlug
export const useRouterOrgSlug = useApiOrgSlug

export function useRouterOrgSlugFor(realSlug: string | null) {
  useSafeApiOrgSlug()
  return realSlug
}
